import { useGetPublicPriceFeeds } from "@/hooks/usePublicProtocol";
import type { CollateralKey } from "@/data/fixtures";
import {
	loadKeeperProviderSnapshots,
	mergeProviderQuotes,
} from "@/lib/oracle/load-keeper-snapshots";
import {
	COLLATERAL_KEYS,
	consensusForAsset,
	fetchAllProviderQuotes,
	type ProviderQuoteRow,
} from "@/lib/oracle/price-providers";
import { AUTO_REFRESH_INTERVAL_MS } from "@/lib/oracle/refresh-policy";
import { useQuery } from "@tanstack/react-query";

const emptyQuotes = (): Record<CollateralKey, ProviderQuoteRow[]> => ({
	ICP: [],
	ckBTC: [],
	ckETH: [],
});

const emptyConsensus = () =>
	Object.fromEntries(
		COLLATERAL_KEYS.map((asset) => [
			asset,
			{ consensus: null, inConsensus: new Set<string>() },
		]),
	) as Record<CollateralKey, ReturnType<typeof consensusForAsset>>;

export function useOracleTransparency() {
	const displayQuery = useGetPublicPriceFeeds();
	const providersQuery = useQuery({
		queryKey: ["v2", "oracleTransparency", "providers"],
		queryFn: async () => {
			const [browserQuotes, keeperSnapshot] = await Promise.all([
				fetchAllProviderQuotes(),
				loadKeeperProviderSnapshots(),
			]);
			const { quotes, keeperUpdatedAt } = mergeProviderQuotes(
				browserQuotes,
				keeperSnapshot,
			);
			const consensusByAsset = Object.fromEntries(
				COLLATERAL_KEYS.map((asset) => [
					asset,
					consensusForAsset(quotes[asset] ?? []),
				]),
			) as Record<CollateralKey, ReturnType<typeof consensusForAsset>>;
			return {
				providerQuotes: quotes,
				consensusByAsset,
				fetchedAt: Date.now(),
				keeperUpdatedAt,
			};
		},
		staleTime: AUTO_REFRESH_INTERVAL_MS,
		refetchInterval: AUTO_REFRESH_INTERVAL_MS,
	});

	return {
		displayFeeds: displayQuery.data?.feeds ?? [],
		prices: displayQuery.data?.prices,
		source: displayQuery.data?.source ?? "unavailable",
		providerQuotes: providersQuery.data?.providerQuotes ?? emptyQuotes(),
		consensusByAsset: providersQuery.data?.consensusByAsset ?? emptyConsensus(),
		fetchedAt: providersQuery.data?.fetchedAt,
		keeperUpdatedAt: providersQuery.data?.keeperUpdatedAt ?? null,
		isLoading: displayQuery.isLoading || providersQuery.isLoading,
		isError: displayQuery.isError || providersQuery.isError,
		refetchProviders: () => providersQuery.refetch(),
	};
}
